import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyP2,
} from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useState } from 'react';
import type { EvidenceVerbPopoverProps } from './evidence-verb-suggestions.types';

export const EvidenceVerbPopover = ({
  verbLabel,
  verbIcon: VerbIcon,
  items,
  onItemClick,
  onItemHover,
  onClose,
  maxHeight,
  isMobile = false,
  chrome = 'card',
}: EvidenceVerbPopoverProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const isItemsOnly = chrome === 'items';

  return (
    <div
      role="menu"
      aria-label={verbLabel}
      className={cn(
        'flex w-full max-w-3xl flex-col',
        !isMobile &&
          !isItemsOnly &&
          'rounded-xl border border-border bg-surface shadow-sm'
      )}
      style={maxHeight === undefined ? undefined : { maxHeight: `${maxHeight}px` }}
    >
      {/* Header */}
      {isItemsOnly ? null : isMobile ? (
        <div className="mb-1 flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            aria-label="Back to verbs"
            className="cursor-pointer text-text-tertiary transition-colors duration-200 hover:text-text-primary"
          >
            <ChevronLeft size={14} className="rtl:-scale-x-100" />
          </button>
          <TypographyP2 className="text-text-tertiary">
            {verbLabel}
          </TypographyP2>
        </div>
      ) : (
        <div className="flex items-center justify-between px-2 pb-0 pt-2.5">
          <div className="flex items-center gap-2 pl-3">
            <VerbIcon size={12} className="text-text-tertiary" />
            <TypographyCaption className="text-text-tertiary">
              {verbLabel}
            </TypographyCaption>
          </div>
          <Button
            variant="ghost"
            size="xs"
            iconOnly
            onClick={onClose}
            aria-label="Close suggestions"
            className="text-text-tertiary"
          >
            <X size={14} />
          </Button>
        </div>
      )}

      {/* Items list — the part that gives way when the card is capped */}
      <div
        className={cn(
          'scrollbar-subtle min-h-0 overflow-y-auto',
          isItemsOnly && '-mx-3 flex flex-col gap-1',
          !isMobile && !isItemsOnly && 'px-2 pb-3'
        )}
      >
        {items.map((item, index) => {
          const isActive = activeIndex === index;
          const isAdjacentToActive =
            activeIndex !== null &&
            (index === activeIndex - 1 || index === activeIndex);
          const isLastItem = index === items.length - 1;

          return (
            <div key={item.id}>
              <Button
                role="menuitem"
                variant="ghost"
                size={isMobile ? 'sm' : undefined}
                className={cn(
                  'group relative flex h-auto w-full items-center justify-between rounded-lg',
                  isMobile
                    ? 'py-2 pl-1 pr-3'
                    : isItemsOnly
                      ? 'rounded-xl px-3 py-2.5 transition-colors duration-200 hover:bg-background-tertiary-hover focus-visible:bg-background-tertiary-hover'
                      : 'p-3',
                  isActive &&
                    (isItemsOnly
                      ? 'bg-background-tertiary-hover'
                      : 'bg-surface-hover')
                )}
                onMouseEnter={() => {
                  setActiveIndex(index);
                  onItemHover?.(item.hoverPreview);
                }}
                onMouseLeave={() => {
                  setActiveIndex(null);
                  onItemHover?.(null);
                }}
                onFocus={() => {
                  setActiveIndex(index);
                  onItemHover?.(item.hoverPreview);
                }}
                onBlur={() => {
                  setActiveIndex(null);
                  onItemHover?.(null);
                }}
                // The composer's own suggestion sheet only renders while the
                // editor holds focus, and a starter click is what seeds it — so
                // the row must not take focus on the way. Same guard the sheet's
                // own rows and the Work home suggestion tray carry.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onItemClick(item.starter, item.triggerSuffix)}
              >
                <TypographyP2 className="min-w-0 truncate text-left text-text-primary">
                  {item.patternLabel}
                </TypographyP2>
                {isActive && (
                  <ChevronRight size={14} className="shrink-0 text-text-secondary rtl:-scale-x-100" />
                )}
              </Button>
              {/* Divider: desktop card only, hidden when adjacent to active item */}
              {!isMobile && !isItemsOnly && !isLastItem && (
                <div
                  className={cn(
                    'mx-3 border-b border-border transition-opacity duration-200',
                    isAdjacentToActive && 'opacity-0'
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
